import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { authService } from '../services/authService';
import { isValidPassword } from '../utils/validation';
import './LoginPage.css';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, loginError, isAuthenticated } = useAppStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/years');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Don't validate password format for temp passwords (they might not match our rules)
    try {
      await login(email, password);
    } catch (err: unknown) {
      // Check if it's a NEW_PASSWORD_REQUIRED challenge
      if (err instanceof Error && err.message === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        return;
      }
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setChangePasswordError(null);

    if (!isValidPassword(newPassword)) {
      setChangePasswordError('Password must be 8–128 characters with at least one uppercase letter, one lowercase letter, and one digit.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError('Passwords do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      const session = await authService.completeNewPassword(newPassword);
      // Store session and redirect
      useAppStore.setState({
        session,
        isAuthenticated: true,
        loginError: null,
      });
    } catch (err: unknown) {
      setChangePasswordError(err instanceof Error ? err.message : 'Failed to set new password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Determine special error states
  const isLockedOut = loginError?.toLowerCase().includes('locked') ?? false;
  const isServiceUnavailable = loginError?.toLowerCase().includes('unavailable') ?? false;

  // Show change password form
  if (needsNewPassword) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1 className="login-title">Set New Password</h1>
          <p style={{ color: 'var(--login-text-muted)', marginBottom: '1.5rem', textAlign: 'center' }}>
            You must set a new password to continue.
          </p>
          <form onSubmit={handleChangePassword} className="login-form" noValidate>
            <div className="form-field">
              <label htmlFor="new-password" className="form-label">New Password</label>
              <input
                id="new-password"
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setChangePasswordError(null); }}
                placeholder="Enter new password"
                autoComplete="new-password"
                disabled={isChangingPassword}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="confirm-password" className="form-label">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setChangePasswordError(null); }}
                placeholder="Confirm new password"
                autoComplete="new-password"
                disabled={isChangingPassword}
                required
              />
            </div>
            {changePasswordError && (
              <div className="error-container" role="alert">
                <p className="error-message">{changePasswordError}</p>
              </div>
            )}
            <button
              type="submit"
              className="login-button"
              disabled={isChangingPassword || !newPassword || !confirmPassword}
            >
              {isChangingPassword ? 'Setting password…' : 'Set Password & Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-title">JAW Photos</h1>
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          <div className="form-field">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isLoading || isLockedOut}
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (validationError) setValidationError(null);
              }}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isLoading || isLockedOut}
              required
            />
            {validationError && (
              <p className="field-error" role="alert">
                {validationError}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading || isLockedOut || !email || !password}
          >
            {isLoading ? (
              <span className="loading-indicator" aria-label="Signing in">
                <span className="spinner" />
                Signing in…
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {loginError && (
          <div className="error-container" role="alert">
            {isLockedOut && (
              <p className="error-message error-lockout">
                Account temporarily locked. Try again in 15 minutes.
              </p>
            )}
            {isServiceUnavailable && (
              <>
                <p className="error-message error-unavailable">
                  Authentication service unavailable. Tap to retry.
                </p>
                <button
                  type="button"
                  className="retry-button"
                  onClick={() => login(email, password)}
                  disabled={isLoading}
                >
                  Retry
                </button>
              </>
            )}
            {!isLockedOut && !isServiceUnavailable && (
              <p className="error-message">{loginError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
